import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, User, Bell, Trash2, CheckCircle, Circle, Save, Key, Monitor, Smartphone, Tablet } from 'lucide-react';
import { admin, type User as UserType, type Reminder, type VisitStats } from '../../lib/api';
import { useAuth } from '../../lib/auth';

const OFFSET_LABELS: Record<string, string> = {
  day_before: '1 day before',
  week_before: '1 week before',
  month_before: '1 month before',
};

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserType | null>(null);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [visitStats, setVisitStats] = useState<VisitStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Editable fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    admin.getUser(id!).then(({ user, reminders, visitStats }) => {
      setUser(user);
      setReminders(reminders);
      setVisitStats(visitStats);
      setFullName(user.full_name || '');
      setEmail(user.email);
      setMobile(user.mobile || '');
      setAddress(user.address || '');
      setIsAdmin(!!user.is_admin);
      setIsActive(!!user.is_active);
    }).catch(err => setError(err.message)).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMsg(''); setSaveErr('');
    setSaveLoading(true);
    try {
      const updated = await admin.updateUser(id!, { full_name: fullName, email, mobile, address, is_admin: isAdmin as any, is_active: isActive as any });
      setUser(updated.user);
      setSaveMsg('User updated successfully!');
    } catch (err: any) {
      setSaveErr(err.message || 'Failed to update user');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(''); setPwErr('');
    if (newPassword.length < 8) { setPwErr('Password must be at least 8 characters'); return; }
    setPwLoading(true);
    try {
      await admin.resetUserPassword(id!, newPassword);
      setPwMsg('Password reset successfully!');
      setNewPassword('');
    } catch (err: any) {
      setPwErr(err.message || 'Failed to reset password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Permanently delete user ${user?.email}? This cannot be undone.`)) return;
    try {
      await admin.deleteUser(id!);
      navigate('/admin/users');
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  if (error) return <div className="card text-red-600 text-center py-10">{error}</div>;
  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/users" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{user.full_name || user.email}</h1>
          <p className="text-gray-500 text-sm">{user.email}</p>
        </div>
        {user.is_admin ? (
          <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
            <Shield className="w-3.5 h-3.5" /> Admin
          </span>
        ) : (
          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
            <User className="w-3.5 h-3.5" /> User
          </span>
        )}
      </div>

      {/* Edit user */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900">Edit User</h2>
        {saveMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{saveMsg}</div>}
        {saveErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{saveErr}</div>}
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Full name</label>
              <input type="text" className="input-field" value={fullName} onChange={e => setFullName(e.target.value)} />
            </div>
            <div>
              <label className="label">Email</label>
              <input type="email" className="input-field" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="label">Mobile</label>
              <input type="tel" className="input-field" value={mobile} onChange={e => setMobile(e.target.value)} />
            </div>
            <div>
              <label className="label">Address</label>
              <input type="text" className="input-field" value={address} onChange={e => setAddress(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm font-medium text-gray-700">Administrator</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm font-medium text-gray-700">Active account</span>
            </label>
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={saveLoading}>
            <Save className="w-4 h-4" /> {saveLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Reset password */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Key className="w-4 h-4 text-blue-600" /> Reset Password</h2>
        {pwMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{pwMsg}</div>}
        {pwErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{pwErr}</div>}
        <form onSubmit={handleResetPassword} className="flex gap-3">
          <input
            type="password"
            className="input-field flex-1"
            placeholder="New password (min. 8 chars)"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
            minLength={8}
          />
          <button type="submit" className="btn-secondary flex-shrink-0" disabled={pwLoading}>
            {pwLoading ? 'Resetting...' : 'Reset'}
          </button>
        </form>
      </div>

      {/* Site visits */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Monitor className="w-4 h-4 text-blue-600" /> Site Visits
        </h2>
        {visitStats ? (
          <>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xl font-bold text-gray-900 tabular-nums">{visitStats.total}</p>
                <p className="text-xs text-gray-500 mt-0.5">Total visits</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg flex flex-col items-center">
                <div className="flex items-center gap-1 text-xl font-bold text-gray-900 tabular-nums">
                  <Monitor className="w-4 h-4 text-gray-400" /> {visitStats.byDevice.desktop || 0}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Desktop</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg flex flex-col items-center">
                <div className="flex items-center gap-1 text-xl font-bold text-gray-900 tabular-nums">
                  <Smartphone className="w-4 h-4 text-gray-400" /> {visitStats.byDevice.mobile || 0}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Mobile</p>
              </div>
            </div>
            {(visitStats.byDevice.tablet ?? 0) > 0 && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Tablet className="w-3.5 h-3.5" /> {visitStats.byDevice.tablet} visit(s) from tablet
              </p>
            )}
            <p className="text-sm text-gray-500">
              Last visit:{' '}
              <span className="font-medium text-gray-900">
                {visitStats.lastVisit ? new Date(visitStats.lastVisit * 1000).toLocaleString() : 'Never'}
              </span>
            </p>
          </>
        ) : (
          <p className="text-gray-400 text-sm">No visit data yet</p>
        )}
      </div>

      {/* User's reminders */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Bell className="w-4 h-4 text-blue-600" /> Reminders ({reminders.length}/5)
        </h2>
        {reminders.length === 0 ? (
          <p className="text-gray-400 text-sm">No reminders</p>
        ) : (
          <div className="space-y-3">
            {reminders.map(r => (
              <div key={r.id} className={`flex items-start gap-3 p-3 border rounded-lg ${r.is_completed ? 'opacity-60' : ''}`}>
                {r.is_completed ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-gray-900 ${r.is_completed ? 'line-through text-gray-400' : ''}`}>{r.title}</p>
                  {r.description && <p className="text-sm text-gray-500 truncate">{r.description}</p>}
                  <p className="text-xs text-gray-400 mt-1">Due: {r.due_date} &bull; Remind: {r.reminder_date} ({OFFSET_LABELS[r.reminder_offset]})</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger zone */}
      {currentUser?.id !== user.id && (
        <div className="card border-red-200 space-y-3">
          <h2 className="font-semibold text-red-600">Danger Zone</h2>
          <p className="text-sm text-gray-500">Permanently delete this user and all their data. This cannot be undone.</p>
          <button onClick={handleDelete} className="btn-danger flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete User
          </button>
        </div>
      )}

      {/* Account info */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-4">Account Details</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">User ID</dt>
            <dd className="font-mono text-xs text-gray-700 truncate max-w-xs">{user.id}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Email verified</dt>
            <dd className={`font-medium ${user.email_verified ? 'text-green-600' : 'text-amber-600'}`}>
              {user.email_verified ? 'Verified' : 'Pending'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Sign-in method</dt>
            <dd className="font-medium text-gray-900">{user.google_id ? 'Google' : 'Email & Password'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Created</dt>
            <dd className="font-medium text-gray-900">{new Date(user.created_at * 1000).toLocaleString()}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Last updated</dt>
            <dd className="font-medium text-gray-900">{new Date(user.updated_at * 1000).toLocaleString()}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
